import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { Controller, set, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import type { InferType } from 'yup'
import { array, number, object, string } from 'yup'

import {
    useCreateTeamMutation,
    useGetTeamByIdQuery,
    useUpdateTeamMutation,
} from '../../services/tenant/teamManagement'
import {
    useGetAllUsersQuery,
    useLazyGetAllUsersQuery,
} from '../../services/tenant/users'
import type { ITenantUser } from '../../services/types'
import type { ITeamMember, TeamStatusType } from '../../types/tenant/team.types'
import { optionsTeamStatus } from '../../utils/selectsOptionsConst'
import Button from '../common/Button'
import Input, { InputStyles } from '../common/Input'
import Modal from '../common/Modal'
import SearchInputWidthDrop from '../common/SearchInputWidthDrop'
import SelectBox from '../common/SelectBox'
import SelectBoxAsynk, { OptionCustomProps } from '../common/SelectBoxAsynk'
import type { UserPartListProps } from '../common/SelectedUsersList'
import SelectedUsersList from '../common/SelectedUsersList'
import { UserRoleEnum } from '@/types'
import { FieldError } from '../ui/FieldError'
import { setApiError } from '@/utils/helpers'

const schema = object({
    name: string().required('Service name is required'),
    leadId: number().required('Lead is required'),
    status: string(),
    members: array().of(number().defined()),
})

type FormType = InferType<typeof schema>

interface AddTeamModalProps {
    isOpen: boolean
    onClose: () => void
    id: number | undefined | null
    setId?: React.Dispatch<React.SetStateAction<number | null>>
}

const AddTeamModal = ({ isOpen, onClose, id, setId }: AddTeamModalProps) => {
    const [getUsers] = useLazyGetAllUsersQuery()
    const [createTeam, { isLoading: createLoading }] = useCreateTeamMutation()
    const [updateTeam, { isLoading: updateLoading }] = useUpdateTeamMutation()

    const { data: teamData, isLoading: teamLoading } = useGetTeamByIdQuery(
        id as number,
        {
            skip: !id,
        },
    )

    const [selectedUsers, setSelectedUsers] = useState<UserPartListProps[]>([])
    const [lead, setLead] = useState<{ value: number; label: string }>()

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        control,
        formState: { errors, isDirty },
        setError,
    } = useForm<FormType>({
        mode: 'onChange',
        resolver: yupResolver(schema),
    })

    const handelClose = () => {
        onClose()
        if (setId) setId(null)
    }

    useEffect(() => {
        if (teamData && id) {
            setValue('name', teamData.data.name)
            setValue('leadId', teamData.data.leadId)
            setSelectedUsers(
                teamData.data.members.map(member => ({
                    id: member.id,
                    name: member.fullName,
                    email: member.email,
                    role: member.roles[0],
                    src: member.photo,
                })) || [],
            )
            setValue('status', teamData.data.status)
            setLead({
                value: teamData?.data?.lead?.id,
                label: teamData?.data?.lead?.fullName,
            })
        } else {
            setValue('status', 'active')
        }
    }, [teamData, id])

    useEffect(() => {
        setValue(
            'members',
            selectedUsers.map(user => user.id),
            {
                shouldDirty: true,
                shouldValidate: true,
            },
        )
    }, [selectedUsers, setValue])

    const removeSelectedUser = (id: number) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== id))
    }

    const onSubmit = async (data: FormType) => {
        const formData = {
            ...data,
            status: data.status as TeamStatusType,
            members: data.members,
        }

        let res = null
        try {
            if (id) {
                res = await updateTeam({ teamId: id, body: formData }).unwrap()
            } else {
                res = await createTeam(formData).unwrap()
            }
            if (res?.success) {
                toast.success('Updated successfully!')
                reset()
                handelClose()
            } else {
                toast.error(res?.message || 'Something went wrong')
                setApiError(res?.errors, setError)
            }
        } catch (error) {
            toast.error('Something went wrong')
            console.error('rejected', error)
        }
    }

    const loadOptionsUsers = async (
        search: string,
        loadedOptions: any,
        { page }: { page: number },
    ) => {
        const res = await getUsers(
            `page=${page}&search=${search}&roles=technicians`,
        ).unwrap()

        return {
            options:
                res?.data.map(user => ({
                    value: user.id,
                    label: user.fullName,
                })) || [],
            hasMore: page != res?.pagination?.maxPages,
            additional: {
                page: page + 1,
            },
        }
    }

    const selectUserSearch = (user: ITeamMember) => {
        if (!selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers(prev => [
                ...prev,
                {
                    id: user.id,
                    name: user.fullName,
                    email: user.email,
                    src: user.photo,
                    role: user.roles[0],
                },
            ])
        }
        // setSelectedUsers()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handelClose}
            title={`${id ? 'Edit' : 'Create'} Team`}
            isLoading={teamLoading}
        >
            <div className="modal__body p-0">
                <div className="custom-form">
                    <div className="custom-form-body">
                        <div className="row gutters-16">
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Team Name"
                                    placeholder="Enter Name..."
                                    error={errors.name?.message}
                                    {...register('name')}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="leadId"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBoxAsynk
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Team Lead"
                                            fetchFunk={loadOptionsUsers}
                                            placeholder={'Select Team Lead'}
                                            error={errors.leadId?.message}
                                            onChange={option => {
                                                setLead(option)
                                                field.onChange(option?.value)
                                            }}
                                            defaultValue={
                                                teamData?.data.lead
                                                    ? {
                                                          value: teamData?.data
                                                              .lead.id,
                                                          label: teamData?.data
                                                              .lead.fullName,
                                                      }
                                                    : undefined
                                            }
                                            value={
                                                lead
                                                    ? {
                                                          value: lead?.value,
                                                          label: lead?.label,
                                                      }
                                                    : undefined
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Status"
                                            options={optionsTeamStatus}
                                            placeholder={'Select Status'}
                                            error={errors.status?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                field.value ||
                                                optionsTeamStatus[0].value
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <hr className="form-separator" />
                            </div>
                            <div className="col-12">
                                <div className="search-widget overflow-visible">
                                    <div className="search-widget--head">
                                        <SearchInputWidthDrop
                                            placeholder="Add members by name or email..."
                                            label="Members"
                                            fetchFunk={getUsers}
                                            selectedItems={selectedUsers.map(
                                                user => user.id,
                                            )}
                                            fetchParams={`roles=${UserRoleEnum.TECHNICIANS}`}
                                            onSelected={selectUserSearch}
                                        />
                                    </div>
                                    <div className="search-widget--body">
                                        <SelectedUsersList
                                            list={selectedUsers}
                                            removeAction={removeSelectedUser}
                                        />
                                    </div>
                                    <FieldError
                                        error={errors.members?.message}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="custom-form-footer justify-end">
                        <div className="flex-group flex-group--xs">
                            <Button
                                type="submit"
                                onClick={handleSubmit(onSubmit)}
                                size="lg"
                                variant="primary-gradient"
                                disabled={!isDirty}
                                loading={createLoading || updateLoading}
                            >
                                <span>
                                    {id ? 'Save Changes' : 'Create Team'}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default AddTeamModal
